import { useEffect } from "react";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const GTM_ID = "GTM-MXKM5SB6";

/**
 * Injects Google Tag Manager into the page.
 * Should only be rendered on public-facing marketing pages,
 * NOT on login pages or authenticated application pages.
 *
 * Replicates the standard GTM snippet but creates elements directly
 * so they can all be cleaned up on unmount during SPA navigation.
 */
export const GoogleTagManager = () => {
  useEffect(() => {
    // Avoid injecting twice if already present
    if (document.getElementById("gtm-script")) return;

    // Initialise dataLayer (same as the GTM snippet)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    // Create the external gtm.js script (the `j` variable in the standard snippet)
    const gtmScript = document.createElement("script");
    gtmScript.id = "gtm-script";
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.insertBefore(gtmScript, document.head.firstChild);

    // GTM noscript fallback — injected after opening <body> tag
    const noscript = document.createElement("noscript");
    noscript.id = "gtm-noscript";
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      document.getElementById("gtm-script")?.remove();
      document.getElementById("gtm-noscript")?.remove();
      window.dataLayer = [];
    };
  }, []);

  return null;
};
